/**
 * @file EmptyState.tsx
 * @description Empty state component shown when a table or list has no data.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { InboxIcon } from 'lucide-react';

interface EmptyStateProps {
  /** Primary message shown in large text */
  message?: string;
  /** Secondary descriptive text */
  description?: string;
  /** Optional CTA rendered below the description */
  action?: React.ReactNode;
  /** Override the default icon */
  icon?: React.ReactNode;
  className?: string;
}

/**
 * EmptyState – shown inside tables/pages when there are zero records.
 *
 * @example
 * <EmptyState
 *   message="No tickets yet"
 *   description="Create your first support ticket to get started."
 *   action={<Button>Create Ticket</Button>}
 * />
 */
export const EmptyState = React.memo(function EmptyState({
  message = 'No results found.',
  description,
  action,
  icon,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-3 py-16 text-center', className)}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {icon ?? <InboxIcon className="h-7 w-7" />}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{message}</p>
        {description && <p className="text-xs text-muted-foreground max-w-xs">{description}</p>}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
});
